import { maxLength, minLength, required } from '@reliance/forms-js/lib/validation';
import { ConfigurationText } from '@reliance/types/models/ConfigurationText';
import { AxiosError } from 'axios';
import { FORM_ERROR } from 'final-form';
import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { Field, Form as FinalForm } from 'react-final-form';
import { alertStore } from '../alert/alertStore';
import api from '../common/api';
import { IStatusResponse } from '../common/reliance-api-service';
import { TextInput } from '../form/TextInput';
import { LANGUAGE_OPTIONS } from './languageOptions';

type AddEditModalProps = {
  facilityId: number;
  configurationText?: ConfigurationText | null;
  onClose(success?: boolean): void;
};

type FormValues = {
  key: string;
  content: string;
  language: string;
};

const save = async (
  data: FormValues,
  props: AddEditModalProps
): Promise<void | { [FORM_ERROR]: string }> => {
  const isEdit = props.configurationText != null && props.configurationText.id != null;
  if (isEdit) {
    try {
      const { data: res } = await api.put<IStatusResponse>('/configuration-text', {
        id: props.configurationText!.id,
        key: data.key,
        content: data.content,
        language: data.language,
        facilityId: props.facilityId,
      });
      if (res != null && typeof res === 'object' && 'success' in res && res.success === false) {
        return { [FORM_ERROR]: 'An unexpected error occurred' };
      }
    } catch (err) {
      const ax = err as AxiosError;
      if (ax.response?.status === 400) {
        return { [FORM_ERROR]: 'Please verify the inputs are valid and try again' };
      }
      if (ax.response?.status === 404) {
        return { [FORM_ERROR]: 'Configuration text not found' };
      }
      if (ax.response?.status === 409) {
        return { [FORM_ERROR]: 'Non unique parameter search' };
      }
      return { [FORM_ERROR]: 'An unexpected error occurred' };
    }
    return;
  }
  try {
    const { data: res } = await api.post<IStatusResponse>('/configuration-text', {
      key: data.key,
      content: data.content,
      language: data.language,
      facilityId: props.facilityId,
    });
    if (res != null && typeof res === 'object' && 'success' in res && res.success === false) {
      return { [FORM_ERROR]: 'An unexpected error occurred' };
    }
  } catch (err) {
    const ax = err as AxiosError;
    if (ax.response?.status === 400) {
      return { [FORM_ERROR]: 'Please verify the inputs are valid and try again' };
    }
    if (ax.response?.status === 409) {
      return { [FORM_ERROR]: 'Duplicate configuration text found' };
    }
    return { [FORM_ERROR]: 'An unexpected error occurred' };
  }
};

const AddEditModal: React.FC<AddEditModalProps> = (props) => {
  const isEdit = props.configurationText != null && props.configurationText.id != null;

  const initialValues: FormValues = {
    key: props.configurationText?.key ?? '',
    content: props.configurationText?.content ?? '',
    language: props.configurationText?.language ?? 'EN-US',
  };

  return (
    <Modal show onHide={() => props.onClose(false)}>
      <FinalForm
        initialValues={initialValues}
        onSubmit={async (data: FormValues) => {
          const result = await save(data, props);
          if (result != null && typeof result === 'object' && FORM_ERROR in result) {
            return result;
          }
          alertStore.success(
            isEdit ? 'Configuration text updated successfully' : 'Configuration text added successfully',
            true
          );
          props.onClose(true);
          return undefined;
        }}
        render={({ handleSubmit, submitting, hasValidationErrors, submitError }) => (
          <form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>
                {isEdit ? 'Edit Configuration Text' : 'Add Configuration Text'}
                {submitting && (
                  <>
                    {' '}
                    <i className="fas fa-spin fa-spinner" />
                  </>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {submitError != null && <Alert variant="danger">{String(submitError)}</Alert>}
              <Row>
                <Col md={6}>
                  <TextInput
                    label="Key"
                    name="key"
                    validationRules={[required, minLength(1), maxLength(50)]}
                  />
                </Col>
                <Col md={6}>
                  <TextInput
                    label="Content"
                    name="content"
                    validationRules={[required, minLength(1), maxLength(5000)]}
                  />
                </Col>
                <Col md={6}>
                  <div className="mb-2">
                    <Form.Label>Language</Form.Label>
                    <Field name="language" validate={required}>
                      {({ input, meta }) => (
                        <>
                          <Form.Select
                            {...input}
                            className={meta.touched && meta.error ? 'is-invalid' : undefined}>
                            {LANGUAGE_OPTIONS.map((opt) => (
                              <option key={opt.code} value={opt.code}>
                                {opt.name}
                              </option>
                            ))}
                          </Form.Select>
                          {meta.touched && meta.error != null && (
                            <div className="invalid-feedback d-block">{meta.error}</div>
                          )}
                        </>
                      )}
                    </Field>
                  </div>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="default" type="button" disabled={submitting} onClick={() => props.onClose(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting || hasValidationErrors}>
                {submitting ? (isEdit ? 'Saving...' : 'Adding...') : isEdit ? 'Save' : 'Add'}
              </Button>
            </Modal.Footer>
          </form>
        )}
      />
    </Modal>
  );
};

export default AddEditModal;
